import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { API } from "../config";
import { useLang } from "../i18n";
import { trackEvent } from "../analytics";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// `compact` is the hero variant: one row, no label, tighter helper text. The
// footer keeps the full-size version.
export default function WaitlistForm({ onJoined, compact = false, testidPrefix = "" }) {
  const { t, lang } = useLang();
  const f = t.footer;
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  // Store which error occurred, not the resolved sentence — otherwise the
  // message stays frozen in whichever language it was raised in when the
  // reader switches EN/FR.
  const [errorKey, setErrorKey] = useState(null);
  const message = errorKey ? f[errorKey] : "";

  const submit = async (e) => {
    e.preventDefault();
    const formLocation = compact ? "hero" : "footer";
    if (!EMAIL_RE.test(email.trim())) {
      setStatus("error");
      setErrorKey("invalid");
      toast(f.invalid);
      return;
    }
    setStatus("submitting");
    setErrorKey(null);
    trackEvent("waitlist_submit", { form_location: formLocation, language: lang });
    try {
      const res = await axios.post(`${API}/waitlist`, { email: email.trim(), locale: lang });
      const joined = res.data.status === "joined" || res.data.status === "already_joined";
      if (joined) {
        trackEvent(
          res.data.status === "joined" ? "generate_lead" : "waitlist_returning",
          { form_location: formLocation, language: lang },
        );
        setStatus("success");
        toast(f.success);
        onJoined && onJoined();
      } else {
        throw new Error("unexpected");
      }
    } catch (err) {
      setStatus("error");
      setErrorKey("error");
      toast(f.error);
    }
  };

  const inputId = compact ? "hero-waitlist-email" : "waitlist-email";

  if (status === "success") {
    return (
      <div className={compact ? "mt-7 w-full max-w-[440px] mx-auto text-center" : "mt-8 w-full max-w-[520px]"}>
        <p
          data-testid={`${testidPrefix}waitlist-success-message`}
          className="ff-serif text-xl"
          style={{ color: "var(--bb-ink)" }}
        >
          {f.success}
        </p>
        <p className="mt-2 ff-sans text-sm" style={{ color: "var(--bb-ink-muted)" }}>{f.successSub}</p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className={compact ? "mt-7 w-full max-w-[440px] mx-auto" : "mt-8 w-full max-w-[520px]"}
      noValidate
    >
      {!compact && (
        <label htmlFor={inputId} className="ff-mono text-xs uppercase tracking-[0.18em]" style={{ color: "var(--bb-ink-faint)" }}>
          {f.waitlistLabel}
        </label>
      )}
      <div className={compact ? "flex flex-col gap-3 sm:flex-row sm:items-center" : "mt-3 flex flex-col gap-3 sm:flex-row sm:items-center"}>
        <Input
          id={inputId}
          aria-label={compact ? f.waitlistLabel : undefined}
          data-testid={`${testidPrefix}waitlist-email-input`}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={f.placeholder}
          className="h-12 rounded-[14px] border px-4 ff-sans text-[15px]"
          style={{ background: "var(--bb-surface)", borderColor: "var(--bb-border-strong)", color: "var(--bb-ink)" }}
        />
        <Button
          type="submit"
          data-testid={`${testidPrefix}waitlist-submit-button`}
          disabled={status === "submitting"}
          className="h-12 rounded-[14px] px-6 ff-sans text-[15px] font-medium transition-colors"
          style={{ background: "var(--bb-red)", color: "#0A0A0A" }}
        >
          {status === "submitting" ? (
            <span className="flex items-center gap-2"><span className="bb-spinner" /> {f.submitting}</span>
          ) : (
            f.join
          )}
        </Button>
      </div>
      <p
        className={`mt-3 ff-sans text-sm ${compact ? "text-center" : ""}`}
        style={{ color: "var(--bb-ink-faint)" }}
      >
        {status === "error" && message ? (
          <span data-testid={`${testidPrefix}waitlist-error-message`}>{message}</span>
        ) : (
          f.helper
        )}
      </p>
    </form>
  );
}
